import { Collection, Database, Q } from '@nozbe/watermelondb';
import { map } from 'rxjs/operators';

import { EntryUserDBServiceOptions } from '../../types/dbService';
import { EntryUserModel } from '../../types/EntryUser';

import Organisation from './Organisation';

class EntryUser {
    private database: Database;
    private collection: Collection<EntryUserModel>;
    private table = 'entry_users';
    private options: EntryUserDBServiceOptions;

    constructor(options: EntryUserDBServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
    }

    getAll() {
        return this.collection.query().fetch();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    getByEntryId(entryId: string) {
        return this.collection.query(Q.where('entry_id', entryId));
    }

    getActiveEntryUsersIdsById(entryId: string) {
        return this.collection
            .query(
                Q.experimentalNestedJoin('entry_users', 'users'),
                Q.experimentalNestedJoin('users', 'organisation_users'),
                Q.and(
                    Q.where('entry_id', entryId),
                    Q.on(
                        'users',
                        Q.on('organisation_users', Q.where('active', true)),
                    ),
                ),
            )
            .observe()
            .pipe(
                map((entryUsers: EntryUserModel[]) =>
                    entryUsers.map((entryUser) => entryUser.userId),
                ),
            );
    }

    async getByParam(param: string, value: any) {
        return this.collection.query(Q.where(param, value)).fetch();
    }

    async prepareAddBatch(membersIds: string[], entryId: string) {
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();

        const { id: organisationID } = organisation[0];

        if (!organisationID) {
            this.options.logger.error(
                `(EntryUser) Organisation not found for prepareAddBatch, entry id: ${entryId}`,
            );
        }

        return membersIds.map((userId) =>
            this.collection.prepareCreate((entryUser) => {
                entryUser.entryId = entryId;
                entryUser.userId = userId;
                entryUser.organisationId = organisationID;
            }),
        );
    }

    async prepareDeleteBatch(membersIds: string[]) {
        return Promise.all(
            membersIds.map(async (userId) => {
                const entryUser = await this.getByID(userId);
                return entryUser.prepareMarkAsDeleted();
            }),
        );
    }
}

export default EntryUser;
